import { useEffect, useState, useCallback, useReducer } from "react";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import { Input } from "./Input";
import { Results } from "./Results";
import { debounce } from "debounce";
import { Button } from "@components/Button";
import { fetchAllProducts } from "./lib/depict-search";
import { filterStringBuilder } from "./lib/aloglia-filter-string";
import { Filter } from "@components/Filter";
import { usePopulateFilters } from "@components/Filter/hooks/usePopulateFilters";
import { TYPE, SIZE, COLOUR_TAGS, CATEGORY } from "@components/Filter/constants";
import { useMobileDetect } from "@hooks/index";
import styles from "./styles/search.module.css";

const PRICE_ASC = "Price: low to high";
const PRICE_DESC = "Price: high to low";
const DISCOUNT = "Discount";

const RENDER_OFFSET = 25;
const searchState = {
  renderedProducts: [],
  fetchedProducts: [],
  allProducts: [],
  loading: true
}

const SEARCH_ACTIONS = {
  SET_FETCHED_PRODUCTS: "@searchNext/SET_FETCHED_PRODUCTS",
  SET_SORTED_PRODUCTS: "@searchNext/SET_SORTED_PRODUCTS",
  RENDER_NEXT_PRODCTS: "@searchNext/RENDER_NEXT_PRODCTS",
  CLEAR_PRODUCTS: "@searchNext/CLEAR_PRODUCTS",
};

function searchReducer(state, action) {
  switch (action.type) {
    case SEARCH_ACTIONS.SET_FETCHED_PRODUCTS:
      return {
        ...state,
        fetchedProducts: action.payload.products.slice(RENDER_OFFSET, action.payload.products.length - 1),
        renderedProducts: action.payload.products.slice(0, RENDER_OFFSET),
        allProducts: action.payload.products,
        loading: false
      };
    case SEARCH_ACTIONS.SET_SORTED_PRODUCTS:
      return {
        ...state,
        fetchedProducts: action.payload.products.slice(RENDER_OFFSET, action.payload.products.length - 1),
        renderedProducts: action.payload.products.slice(0, RENDER_OFFSET),
      };
    case SEARCH_ACTIONS.RENDER_NEXT_PRODUCTS:
      return { 
        ...state,
        fetchedProducts: state.fetchedProducts.slice(RENDER_OFFSET, state.fetchedProducts.length - 1),
        renderedProducts: [...state.renderedProducts, ...state.fetchedProducts.slice(0, RENDER_OFFSET)]
      };
    case SEARCH_ACTIONS.CLEAR_PRODUCTS:
      return {
        renderedProducts: [],
        fetchedProducts: [],
        allProducts: [],
       };
    default:
      return state;
  }
}


export function SearchView() {
  const router = useRouter();
  const currentDevice = useMobileDetect();
  const [state, dispatch] = useReducer(searchReducer, searchState);
  const { currency } = state;
  const [ showMobileFilter, setShowMobileFilter ] = useState(false);
  const [ result, setResult ] = useState([]);
  const [ searchTerm, setSearchTerm ] = useState('');
  const [ productFiltering, setProductFiltering] = useState({prevFilters: undefined, next: []});
  const { filteredValues, filterLabels, sortBy, setSortBy, onChange } = usePopulateFilters(productFiltering.next, productFiltering.prevFilters);
  const { locale } = router;

  useEffect(() => {
     (async () => {
      const currency_code = Cookies.get('cart_currency') || 'CAD';
      const productsToMakeFilters = [];
      const filterString = filterStringBuilder(filteredValues)
      const fetchedProducts = await fetchAllProducts(searchTerm, filterString, locale, currency_code);
    
      if (fetchAllProducts.length > 0)
      dispatch({
        type: SEARCH_ACTIONS.SET_FETCHED_PRODUCTS,
        payload: {
          products: fetchedProducts
        }
      });

      if (fetchedProducts.length > 0) {
        setProductFiltering({
          prevFilters: {...filteredValues},
          next: fetchedProducts
        });
      }
    })();
  },[searchTerm, filteredValues, locale, currency]);


  useEffect(() => {
    const sortAsc = (list, value) => list.sort((a, b) => a.sortBy[value] - b.sortBy[value]);
    const sortDesc = (list, value) => list.sort((a, b) => b.sortBy[value] - a.sortBy[value]);
  
    (() => {
      let productList = [...state.allProducts];
      if (sortBy === PRICE_ASC) productList = sortAsc(productList, "price");
      if (sortBy === PRICE_DESC) productList = sortDesc(productList, "price");
      if (sortBy === DISCOUNT) productList = sortDesc(productList, "discount");

      dispatch({
        type: SEARCH_ACTIONS.SET_SORTED_PRODUCTS,
        payload: {
          products: productList
        }
      });
    })();
  }, [sortBy]);

  const handleChange = (term) => {
    setSearchTerm(term);
  }

  const onClickFilterToggle = () => {
    setShowMobileFilter(!showMobileFilter);
  }

  
  const filterMobileStyles = `
    ${styles.mobileFilter}
    ${showMobileFilter ? styles.hide : ""}
  `;

  const toggleFilterDisplay = `
    ${styles.filters}
    ${showMobileFilter ? styles.show : ""}
  `;

  const toggleResultsDisplay = `
    ${styles.results}
    ${showMobileFilter ? styles.hide : ""}
  `;

  return (
    <section className={styles.container}>
      <section className={filterMobileStyles}>
        <Input
              searchTerm={searchTerm}
              handleChange={debounce(handleChange, 500)}
              clearSelection={()=>{}}
              currency={currency}
              dispatch={()=>{}}
            />
            <div onClick={onClickFilterToggle}>
              <p >
                + more filters
              </p>
            </div>
      </section>
      <section className={currentDevice.isMobile() ? toggleFilterDisplay : styles.filters }>
        {Object.keys(filterLabels).length > 0 && (
        <div>
          <div className={currentDevice.isMobile() ? styles.filterCloseBtn : styles.hide } onClick={onClickFilterToggle}>
            <p>
              close
            </p>
          </div>
          <Input
              searchTerm={searchTerm}
              handleChange={debounce(handleChange, 500)}
              clearSelection={()=>{}}
              currency={currency}
              dispatch={()=>{}}
            />
             <Filter
              activeFilters={[CATEGORY, TYPE, COLOUR_TAGS, SIZE ]}
              products={state.allProducts}
              filterLabels={filterLabels}
              filteredValues={filteredValues}
              onChange={onChange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              searchView={true}
            />
        </div>
      )}
      </section>
      <section className={currentDevice.isMobile() ? toggleResultsDisplay : styles.results}>
        <div className={styles.resultsGrid}>
        {state.allProducts?.length === 0 && Object.keys(filterLabels).length > 0 && (
          <div className={styles.noResults}>
              <h3>Sorry, We could not find anything in stock that matched your search.</h3>
          </div>
        )} 
          <Results
            searchTerm={searchTerm}
            products={state.renderedProducts}
            isLoading={state.loading}
            collection={filteredValues.category[0]}
            colour={filteredValues.colourTags[0]}
          />
          
          {state.fetchedProducts.length > 0 && !state.loading && (
            <div className={styles.buttonContainer}>
              <Button
                variant="secondary"
                description="load"
                descriptionText="Click to load more search results"
                handleClick={() => dispatch({ type: SEARCH_ACTIONS.RENDER_NEXT_PRODUCTS})}
              >
                Show More
              </Button>
           </div>
        )}
        </div>
      </section>
    </section>
  );
}
